import { makeAutoObservable } from "mobx";
import { Camera, Part, Texture } from "../types";

export interface ChangeEntry {
  category: "material" | "location" | "camera-location" | "verification";
  cameraName: string;
  partName?: string;
  textureName?: string;
  description: string;
}

export class InsideMapsConfigStore {
  cameras: Camera[] = [];
  selectedCameraId: string | null = null;
  selectedPartId: string | null = null;
  selectedTextureIndex: number | null = null;
  isDirty = false;
  selectedProductOfferingId: string | null = null;
  /** Deep clone of cameras at load/save time, used for diff computation */
  private _originalCameras: Camera[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  loadFromJson(json: string | null | undefined): void {
    if (!json) {
      this.cameras = [];
      this._originalCameras = [];
      this.selectedCameraId = null;
      this.selectedPartId = null;
      this.selectedTextureIndex = null;
      this.isDirty = false;
      return;
    }
    try {
      const parsed = JSON.parse(json);
      // Support both { cameras: [...] } and raw array formats
      const cameras: Camera[] = Array.isArray(parsed) ? parsed : (parsed.cameras ?? []);
      this.cameras = cameras;
      this._originalCameras = deepCloneCameras(cameras);
      this.isDirty = false;
      // Auto-select first camera/part if available
      if (cameras.length > 0) {
        this.selectedCameraId = cameras[0].id;
        if (cameras[0].parts.length > 0) {
          this.selectedPartId = cameras[0].parts[0].id;
          this.selectedTextureIndex = 0;
        }
      }
    } catch (e) {
      console.error("Failed to parse insideMapsConfig JSON:", e);
      this.cameras = [];
    }
  }

  toJson(): string {
    const filtered = filterDataForExport(this.cameras);
    return JSON.stringify({ cameras: filtered });
  }

  toFullJson(): string {
    return JSON.stringify({ cameras: this.cameras });
  }

  // --- Computed ---

  get selectedCamera(): Camera | undefined {
    return this.cameras.find((c) => c.id === this.selectedCameraId);
  }

  get selectedPart(): Part | undefined {
    return this.selectedCamera?.parts.find((p) => p.id === this.selectedPartId);
  }

  get selectedTexture(): Texture | undefined {
    if (this.selectedTextureIndex === null || !this.selectedPart) return undefined;
    return this.selectedPart.textures[this.selectedTextureIndex];
  }

  get totalTextures(): number {
    return this.cameras.flatMap((c) => c.parts).flatMap((p) => p.textures).length;
  }

  get reviewedTextures(): number {
    return this.cameras
      .flatMap((c) => c.parts)
      .flatMap((p) => p.textures)
      .filter((t) => t.verified || t.skipped).length;
  }

  get totalParts(): number {
    return this.cameras.flatMap((c) => c.parts).length;
  }

  get partsWithLocations(): number {
    return this.cameras.flatMap((c) => c.parts).filter((p) => p.locationIds?.nonEmpty).length;
  }

  get camerasWithLocation(): number {
    return this.cameras.filter((c) => c.locationId).length;
  }

  get completionPercentage(): number {
    if (this.totalTextures === 0) return 0;
    return Math.round((this.reviewedTextures / this.totalTextures) * 1000) / 10;
  }

  /** Computes a list of user-facing changes between current state and the last saved/loaded state */
  get changes(): ChangeEntry[] {
    return computeChanges(this._originalCameras, this.cameras);
  }

  // --- Actions ---

  toggleCamera(cameraId: string): void {
    if (this.selectedCameraId === cameraId) {
      // Collapse: clicking the already-expanded camera collapses it
      this.selectedCameraId = null;
      this.selectedPartId = null;
      this.selectedTextureIndex = null;
    } else {
      this.selectCamera(cameraId);
    }
  }

  selectCamera(cameraId: string): void {
    this.selectedCameraId = cameraId;
    const camera = this.cameras.find((c) => c.id === cameraId);
    if (camera && camera.parts.length > 0) {
      this.selectedPartId = camera.parts[0].id;
      this.selectedTextureIndex = 0;
    } else {
      this.selectedPartId = null;
      this.selectedTextureIndex = null;
    }
  }

  selectPart(partId: string): void {
    this.selectedPartId = partId;
    this.selectedTextureIndex = 0;
  }

  selectTexture(index: number): void {
    this.selectedTextureIndex = index;
  }

  setMaterialVariantIds(cameraId: string, partId: string, textureIndex: number, mvIds: string[]): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part && part.textures[textureIndex]) {
      const texture = part.textures[textureIndex];
      texture.materialVariantIds = mvIds;
      texture.mvId = mvIds[0] || null;
      texture.verified = mvIds.length > 0;
      this.isDirty = true;
    }
  }

  setHardcodedMaterialVariantIds(cameraId: string, partId: string, textureIndex: number, csvIds: string): void {
    const mvIds = csvIds
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part && part.textures[textureIndex]) {
      const texture = part.textures[textureIndex];
      texture.materialVariantIds = mvIds;
      texture.mvId = mvIds[0] || null;
      texture.verified = mvIds.length > 0;
      texture.hardcoded = true;
      this.isDirty = true;
    }
  }

  verifyTexture(cameraId: string, partId: string, textureIndex: number, verified: boolean): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part && part.textures[textureIndex]) {
      part.textures[textureIndex].verified = verified;
      if (verified) part.textures[textureIndex].skipped = false;
      this.isDirty = true;
    }
  }

  skipTexture(cameraId: string, partId: string, textureIndex: number, skipped: boolean): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part && part.textures[textureIndex]) {
      part.textures[textureIndex].skipped = skipped;
      if (skipped) part.textures[textureIndex].verified = false;
      this.isDirty = true;
    }
  }

  setPartLocationIds(cameraId: string, partId: string, locationIds: string[]): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part) {
      part.locationIds = locationIds;
      this.isDirty = true;
    }
  }

  togglePartLocation(cameraId: string, partId: string, locationId: string): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part) {
      if (!part.locationIds) part.locationIds = [];
      if (part.locationIds.includes(locationId)) {
        part.locationIds = part.locationIds.filter((id) => id !== locationId);
      } else {
        part.locationIds.push(locationId);
      }
      this.isDirty = true;
    }
  }

  setHardcodedLocationIds(cameraId: string, partId: string, csvIds: string): void {
    const locationIds = csvIds
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (part) {
      part.locationIds = locationIds;
      this.isDirty = true;
    }
  }

  setCameraLocationId(cameraId: string, locationId: string | undefined): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    if (camera) {
      camera.locationId = locationId;
      this.isDirty = true;
    }
  }

  copyMaterialsDown(cameraId: string, partId: string, fromTextureIndex: number): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (!part) return;
    const sourceTexture = part.textures[fromTextureIndex];
    if (!sourceTexture?.materialVariantIds?.length) return;
    for (let i = fromTextureIndex + 1; i < part.textures.length; i++) {
      part.textures[i].materialVariantIds = [...sourceTexture.materialVariantIds];
      part.textures[i].mvId = sourceTexture.materialVariantIds[0] || null;
    }
    this.isDirty = true;
  }

  clearAllMaterials(cameraId: string, partId: string): void {
    const camera = this.cameras.find((c) => c.id === cameraId);
    const part = camera?.parts.find((p) => p.id === partId);
    if (!part) return;
    for (const texture of part.textures) {
      texture.materialVariantIds = [];
      texture.mvId = null;
      texture.verified = false;
      texture.hardcoded = false;
    }
    this.isDirty = true;
  }

  bulkSetMaterialVariantIds(
    assignments: Array<{ cameraId: string; partId: string; textureIndex: number; mvIds: string[] }>,
  ): void {
    for (const { cameraId, partId, textureIndex, mvIds } of assignments) {
      const camera = this.cameras.find((c) => c.id === cameraId);
      const part = camera?.parts.find((p) => p.id === partId);
      if (part && part.textures[textureIndex]) {
        const texture = part.textures[textureIndex];
        texture.materialVariantIds = mvIds;
        texture.mvId = mvIds[0] || null;
        texture.verified = mvIds.length > 0;
      }
    }
    this.isDirty = true;
  }

  setSelectedProductOfferingId(id: string | null): void {
    this.selectedProductOfferingId = id;
  }

  markSaved(): void {
    this.isDirty = false;
    this._originalCameras = deepCloneCameras(this.cameras);
  }

  reset(): void {
    this.cameras = [];
    this._originalCameras = [];
    this.selectedCameraId = null;
    this.selectedPartId = null;
    this.selectedTextureIndex = null;
    this.isDirty = false;
    this.selectedProductOfferingId = null;
  }
}

/** Filter data for export/save: remove incomplete entries and legacy fields */
function filterDataForExport(cameras: Camera[]): Camera[] {
  return cameras
    .filter((camera) => camera.locationId)
    .map((camera) => ({
      id: camera.id,
      cameraName: camera.cameraName,
      locationId: camera.locationId,
      alternateViewFor: camera.alternateViewFor,
      displayName: camera.displayName,
      parts: camera.parts
        .filter((part) => part.locationIds?.nonEmpty)
        .map((part) => ({
          id: part.id,
          name: part.name,
          defaultTextureName: part.defaultTextureName,
          defaultTextureIndex: part.defaultTextureIndex,
          locationIds: part.locationIds,
          textures: part.textures
            .filter((texture) => texture.materialVariantIds?.nonEmpty)
            .map((texture) => ({
              name: texture.name,
              index: texture.index,
              materialVariantIds: texture.materialVariantIds,
              verified: texture.verified,
            })),
        })),
    }));
}

function deepCloneCameras(cameras: Camera[]): Camera[] {
  return cameras.map((c) => ({
    ...c,
    parts: c.parts.map((p) => ({
      ...p,
      locationIds: p.locationIds ? [...p.locationIds] : undefined,
      textures: p.textures.map((t) => ({
        ...t,
        materialVariantIds: t.materialVariantIds ? [...t.materialVariantIds] : undefined,
      })),
    })),
  }));
}

function arraysEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  return aa.every((v, i) => v === bb[i]);
}

function computeChanges(original: Camera[], current: Camera[]): ChangeEntry[] {
  const changes: ChangeEntry[] = [];
  const origMap = new Map(original.map((c) => [c.id, c]));

  for (const cam of current) {
    const origCam = origMap.get(cam.id);
    if (!origCam) continue;

    // Camera location change
    if (cam.locationId !== origCam.locationId) {
      changes.push({
        category: "camera-location",
        cameraName: cam.cameraName,
        description: origCam.locationId
          ? cam.locationId
            ? `Location changed from "${origCam.locationId}" to "${cam.locationId}"`
            : `Location "${origCam.locationId}" removed`
          : `Location set to "${cam.locationId}"`,
      });
    }

    const origPartMap = new Map(origCam.parts.map((p) => [p.id, p]));

    for (const part of cam.parts) {
      const origPart = origPartMap.get(part.id);
      if (!origPart) continue;

      // Part location changes
      if (!arraysEqual(part.locationIds, origPart.locationIds)) {
        const added = (part.locationIds ?? []).filter((id) => !(origPart.locationIds ?? []).includes(id));
        const removed = (origPart.locationIds ?? []).filter((id) => !(part.locationIds ?? []).includes(id));
        const parts: string[] = [];
        if (added.length) parts.push(`added ${added.length}`);
        if (removed.length) parts.push(`removed ${removed.length}`);
        changes.push({
          category: "location",
          cameraName: cam.cameraName,
          partName: part.name,
          description: `Locations ${parts.join(", ")}`,
        });
      }

      // Texture-level changes
      for (let i = 0; i < part.textures.length; i++) {
        const tex = part.textures[i];
        const origTex = origPart.textures[i];
        if (!origTex) continue;

        // Material changes
        if (!arraysEqual(tex.materialVariantIds, origTex.materialVariantIds)) {
          const oldIds = origTex.materialVariantIds ?? [];
          const newIds = tex.materialVariantIds ?? [];
          const added = newIds.filter((id) => !oldIds.includes(id));
          const removed = oldIds.filter((id) => !newIds.includes(id));
          const parts: string[] = [];
          if (added.length) parts.push(`${added.length} added`);
          if (removed.length) parts.push(`${removed.length} removed`);
          changes.push({
            category: "material",
            cameraName: cam.cameraName,
            partName: part.name,
            textureName: tex.name,
            description: `Materials: ${parts.join(", ")}`,
          });
        }

        // Verification changes
        if (tex.verified !== origTex.verified || tex.skipped !== origTex.skipped) {
          const status = tex.verified ? "verified" : tex.skipped ? "skipped" : "unverified";
          changes.push({
            category: "verification",
            cameraName: cam.cameraName,
            partName: part.name,
            textureName: tex.name,
            description: `Marked as ${status}`,
          });
        }
      }
    }
  }

  return changes;
}
